import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import {
  selectActionsUsed,
  selectCurrentPlayer,
  selectCurrentTeam,
  selectMetadataCells,
  selectMetadataUsers,
  selectMovements,
  selectPlayerFood,
  selectResearchLevels,
  selectStandingProduction,
  selectViewerId,
} from "src/store/game"
import { foodIncomeBreakdown, foodIncomeFor, foodStaffingFor, foodUpkeepBreakdown, foodUpkeepFor } from "src/utils/foodOutlook"
import { cellDisplayName } from "src/utils/maps"
import { maxActions as maxActionsFor } from "src/utils/gameModifiers"

import Modal from "src/components/modals/Modal"

import "./StatDetailModal.css"
import "src/components/FoodOutlook.css"

/* The story behind a top-bar number. One modal, three topics — food, actions and production —
   each answering the question the chip raises: where does it come from, where does it go, when
   does it run out. Production (owner's U4, 2026-09-07) is what is on order, town by town: the chip
   says only how many units are queued, and this is where "what, where, and for how much" lives.
   (Research had a topic here once; its chip now opens ResearchModal, where the points are spent.) */

const StatDetailModal = ({ topic }) => {
  const { t } = useTranslation()
  const gameConfig = useSelector(selectGameConfig)
  const cells = useSelector(selectMetadataCells)
  const userId = useSelector(selectViewerId)
  const team = useSelector(selectCurrentTeam)
  const player = useSelector(selectCurrentPlayer)
  const food = useSelector(selectPlayerFood)
  const researchLevels = useSelector(selectResearchLevels)
  const actionsUsed = useSelector(selectActionsUsed)
  const movements = useSelector(selectMovements)
  const users = useSelector(selectMetadataUsers)
  const standing = useSelector(selectStandingProduction)

  const rules = gameConfig?.rules_config || {}
  const race = player?.race

  const row = (label, value, tone = "", title = undefined) => (
    <div className={`stat-detail-row ${tone}`} key={label} title={title}>
      <span>{label}</span>
      <span className="stat-detail-value">{value}</span>
    </div>
  )

  let title = ""
  let body = null

  if (topic === "food") {
    const income = foodIncomeFor({ gameConfig, cells, userId, team, race, researchLevels, users, movements })
    const upkeep = foodUpkeepFor({ gameConfig, cells, movements, userId, team, race })
    const net = income - upkeep
    const roundsLeft = net < 0 ? Math.floor(food / -net) : null
    // The lines behind the two figures (owner's Q13b): which cities harvest what and at what
    // share of their potential, and which units eat what — the road included.
    const harvests = foodIncomeBreakdown({ gameConfig, cells, userId, team, race, researchLevels, users, movements })
    const eaters = foodUpkeepBreakdown({ gameConfig, cells, movements, userId, team })
    // The same bar the HUD chip shows (owner's S11): how staffed the fields are, in one line,
    // above the per-city list that itemises it.
    const staffing = foodStaffingFor({ gameConfig, cells, userId, team })
    const short = staffing.required > 0 && staffing.missing > 0
    title = t(team === "zombie" ? "game.statDetail.meatTitle" : "game.statDetail.foodTitle")
    body = (
      <>
        {row(t("game.statDetail.stock"), food)}
        {row(t("game.statDetail.income"), `+${income}`, "good")}
        {row(t("game.statDetail.upkeep"), `-${upkeep}`, "bad")}
        {row(t("game.statDetail.net"), `${net >= 0 ? "+" : ""}${net}`, net >= 0 ? "good" : "bad")}
        {short && (
          <div className="food-staffing stat-detail-staffing" role="img" aria-label={t("game.food.staffing", staffing)}>
            <span className="food-staffing-bar">
              <span className="food-staffing-fill" style={{ width: `${staffing.share}%` }} />
            </span>
            <span className="food-staffing-text">{t("game.food.staffing", staffing)}</span>
          </div>
        )}
        {harvests.length > 0 && (
          <div className="stat-detail-list">
            <p className="section-label">{t("game.statDetail.harvestBy")}</p>
            {harvests.map((entry) => {
              const [col, rowNum] = entry.key.split("-").map(Number)
              const name = cellDisplayName({ column: col, row: rowNum, held: true })
              const share = entry.potential > 0 ? Math.round((entry.actual / entry.potential) * 100) : 100
              // Every row is one number in the same place (owner, 2026-09-08). It used to read
              // "+850 de 900 (94% · faltan trabajadores)" for an understaffed farm and "+900" for
              // a full one, so the column that was meant to be scanned had two shapes in it and
              // the wide one was the one that needed reading. The colour carries the warning now,
              // amber like every other economy warning, and the sentence moved to the hover — the
              // staffing bar above already says it in words for the whole realm.
              const besieged = entry.besieged
              const tone = besieged ? "bad" : share < 100 ? "warn" : "good"
              const title = besieged
                ? t("game.statDetail.harvestBesiegedTitle")
                : share < 100
                  ? t("game.statDetail.harvestShortTitle", {
                      actual: entry.actual,
                      potential: entry.potential,
                      share,
                    })
                  : undefined
              // The siege marker goes on the *label*, never the value: the label column already
              // varies in width, and the point of the change is that the numbers line up.
              const label = `${name} · ${entry.key}${besieged ? " ⚔" : ""}`
              return row(label, `+${entry.actual}`, tone, title)
            })}
          </div>
        )}
        {eaters.length > 0 && (
          <div className="stat-detail-list">
            <p className="section-label">{t("game.statDetail.eatenBy")}</p>
            {eaters.map((line) =>
              row(
                `${line.number} × ${t(`city.units.${line.unitType}`, { defaultValue: line.unitType })}${
                  line.inTransit ? ` · ${t("game.statDetail.onTheMarch")}` : ""
                }`,
                `-${line.upkeep}`,
                "bad"
              )
            )}
          </div>
        )}
        {roundsLeft != null && (
          <p className="stat-detail-warning">⚠ {t("game.statDetail.famineIn", { count: roundsLeft })}</p>
        )}
        {/* Kill-meat is a zombie rule — Game content sheet, Tropas: carne per kill is 10 for the
            horde and nothing for humans, whose raiding economy (incursiones) is a separate,
            still-unbuilt mechanic. Telling a human player their kills feed them was a lie. */}
        <p className="stat-detail-note">
          {t(team === "zombie" ? "game.statDetail.meatNote" : "game.statDetail.foodNoteHuman", {
            meat: rules.MEAT_PER_KILL ?? 10,
          })}
        </p>
      </>
    )
  } else if (topic === "actions") {
    const maxActions = maxActionsFor({ gameConfig, race })
    title = t("game.statDetail.actionsTitle")
    body = (
      <>
        {row(t("game.statDetail.actionsUsed"), `${actionsUsed} / ${maxActions}`)}
        <div className="stat-detail-list">
          <p className="section-label">{t("game.statDetail.costsAction")}</p>
          <p className="stat-detail-note">
            ⚔ {t("game.mapAction.march")} · 🚩{" "}
            {t("game.mapAction.settle")} · 🚀 {t("game.mapAction.strike")} · 🤝 {t("game.statDetail.trade")}
          </p>
          <p className="section-label">{t("game.statDetail.freeActions")}</p>
          <p className="stat-detail-note">{t("game.statDetail.freeList")}</p>
        </div>
        <p className="stat-detail-note">{t("game.statDetail.actionsNote")}</p>
      </>
    )
  } else if (topic === "production") {
    // Humans pay in workers held on the producing cell, zombies from the meat pool — the same
    // split `selectStandingProduction` makes, discounted the way the server charges.
    const currency = team === "human" ? t("game.standingOrders.workers") : t("game.standingOrders.meat")
    const unitName = (unitType) => t(`city.units.${unitType}`, { defaultValue: unitType })
    const listUnits = (counts) =>
      Object.entries(counts || {})
        .map(([unitType, number]) => t("game.standingOrders.unitCount", { count: number, unit: unitName(unitType) }))
        .join(", ")
    title = t("game.statDetail.productionTitle")
    body = (
      <>
        {row(t("game.statDetail.productionQueued"), standing.units)}
        {row(t("game.statDetail.productionCost"), `${standing.cost} ${currency}`, "bad")}
        {standing.cities.length > 0 && (
          <div className="stat-detail-list">
            <p className="section-label">{t("game.statDetail.productionBy")}</p>
            {standing.cities.map((city) => {
              const [col, rowNum] = city.coordinates.split("-").map(Number)
              const name = cellDisplayName({ column: col, row: rowNum, held: true })
              return row(`${name} · ${city.coordinates}`, `${listUnits(city.byUnit)} · ${city.cost} ${currency}`)
            })}
          </div>
        )}
        <p className="stat-detail-note">{t("game.standingOrders.title")}</p>
      </>
    )
  }

  return (
    <Modal width="min(480px, 90%)" isolated={false} scope="map" top="10vh" maxHeight="76vh">
      <div className="stat-detail">
        <h2>{title}</h2>
        {body}
      </div>
    </Modal>
  )
}

export default StatDetailModal
